import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import moment from "moment";
import { BehaviorSubject, of as observableOf, of, Subject, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../environments/environment";
import { FSP_COMMUNITY_STRING_ID, MessageEmbed, resolveLocalizedString } from "../models/community.model";
import { segmentMessageByMentions } from "../utils/mention.utils";
import { splitPostSignature } from "../utils/post-signature.utils";
import { BetaCommunityService } from "./beta-community.service";
import { HelperService } from "./helper.service";
import { LanguageService } from "./language.service";
import { StorageKeys, StorageService } from "./storage.service";
import { UserService } from "./user.service";
import { WindowService } from "./window.service";
import { mentionIdsOf } from "../utils/mention.utils";
@Injectable({
  providedIn: "root",
})
export class CommunityService {
  sanitizer = inject(DomSanitizer);
  public postDataUpdated$: Subject<any> = new Subject();

  public isMobile: boolean;
  public lastVisiblePost: any;
  public lastVisibleRecentReply: any;
  public lastVisiblePostsByKey: any = {};

  public connectionMap: any = {};
  public connectionRequests: Array<any>;
  public availableSkills: Array<any>;
  public taggableChannels: Array<any>;
  public taggableChannelsMap: any = {};
  public skillsMap: any = {};
  public topicMap: any = {};
  public topics: Array<any>;
  public extraChannelsMap: any = {};
  /**
   * The four built-in sidebar filters that appear on every community's channel list.
   * These are NOT channels in the data-model sense — a post never gets tagged with
   * `channels: ["all_posts"]`. They're view modes over the feed. Admins add real
   * channels via the community-manager UI; those go into MySQL `community_channels`.
   *
   * Removed in DENG-728: `learning_studio_one` + `collaboration` (FSP-only content
   * channels — if Fender wants them back they can create real channels for them),
   * and the `Community.extra_channels` doc field (no longer configurable).
   */
  public extraChannels: Array<any> = [
    {
      id: "all_posts",
      string_id: "all_posts",
      title: "All Posts",
      active: true,
      public: true,
      beta: true,
    },
    {
      id: "my_posts",
      string_id: "my_posts",
      title: "My Posts",
      active: true,
      public: true,
      beta: true,
    },
    {
      id: "pinned_posts",
      string_id: "pinned_posts",
      // Shown as "saved" — the id and the `pins` field behind it stay "pinned".
      title: "Saved Posts",
      active: true,
      public: true,
    },
  ];

  public userHasSkills: boolean = false;

  public currentlyPlayingWaveformComponent: any;
  public displayAudioWaveforms: boolean = false;
  public cachedSearchResults: Array<any>;
  public cachedSearchParentsMap: any = {};
  public cachedSearchPage: number;
  public cachedFirebaseProfiles: any = {};
  public cachedProfiles: any = {};
  public cachedNames: any = {};
  public cachedPhotos: any = {};
  public playingAudioFile: any;

  // Initialised, not left undefined: the document watchers iterate this the moment a post
  // they are watching changes, which can happen before — or instead of — a successful load.
  // A failed query (a missing Firestore index, say) left this undefined and turned every
  // watcher callback into "Cannot read properties of undefined (reading 'forEach')".
  public activeParentPosts: Array<any> = [];
  public activeParentThreadMap: any = {};
  public activeParentThreadMapTruncated: any = {};

  /**
   * Whether the feed being loaded can do without each thread's replies.
   *
   * Set by the channel from its layout: the list rows show only a reply count and a "latest
   * activity" date, and parent posts carry both (`reply_count`, `last_reply_at` — maintained
   * by the reply-stats function in workspace-notifications). Downloading the replies for those
   * rows was the single biggest cost of a feed load: one busy thread is 200+ replies and over
   * half a megabyte. The detailed layout renders replies inline, so it leaves this off.
   *
   * Posts written before the backfill have no `reply_count` and still get their replies
   * fetched, so this degrades to the old behaviour rather than showing wrong counts.
   */
  public deferRepliesInFeed = false;

  public activeParentPostTypes: any = {};

  public activePostsLimitReached: boolean = false;
  public activePostDetailSubscription: any;
  public activePostsSubscription: any;
  public displayedPostsMap: any = {};
  public processedReplies: any = {};
  public parentThreadKeys: any;

  public selectedThread: any;

  public cachedModeratingPosts: any;

  protected betaCommunityService = inject(BetaCommunityService);
  protected languageService = inject(LanguageService);

  public pendingPosts: Array<any> = [];

  private getValidStringValue(value: any): string | null {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
    return null;
  }

  private getChannelId(channel: any): string | null {
    if (!channel) return null;
    return this.getValidStringValue(channel.string_id) || this.getValidStringValue(channel.id);
  }

  private _hasSetupCommunity: boolean;
  set hasSetupCommunity(value) {
    if (value) {
      this.storage.setItem(StorageKeys.HAS_SETUP_COMMUNITY, value);
    } else {
      this.storage.removeItem(StorageKeys.HAS_SETUP_COMMUNITY);
    }
    this._hasSetupCommunity = value;
  }

  get hasSetupCommunity() {
    return this._hasSetupCommunity;
  }

  /**
   * "Has this user completed the onboarding wizard for this specific community?" — the
   * per-community successor to the flat `hasSetupCommunity` boolean. Reads from
   * `profile.hasSetupCommunities[stringId]`. Falls back to the legacy global flag ONLY
   * for FSP so users who onboarded before per-community tracking existed don't get a
   * repeat FSP wizard.
   *
   * Added 2026-08-31 with the Tone Master Pro onboarding wizard — without this, a user
   * who ran the FSP wizard would silently skip every other community's welcome flow
   * because the old global flag was true for their entire session.
   */
  hasSetupCommunityFor(stringId: string): boolean {
    if (!stringId) return false;
    const perCommunity = this.profile?.hasSetupCommunities?.[stringId];
    if (perCommunity === true) return true;
    if (stringId === FSP_COMMUNITY_STRING_ID) {
      // Legacy compat: pre-2026-08-31 FSP-only writes only set the boolean.
      return !!(this.profile?.hasSetupCommunity || this._hasSetupCommunity);
    }
    return false;
  }

  public profileUpdated$: Subject<any> = new Subject();
  public notificationReceived$: Subject<any> = new Subject();
  public firebaseProfile$: BehaviorSubject<any> = new BehaviorSubject(null);
  private _profile: any;
  set profile(value) {
    if (value) {
      this.profileUpdated$.next(value);
      this.storage.setItem(StorageKeys.COMMUNITY_PROFILE, value);
    } else {
      this.storage.removeItem(StorageKeys.COMMUNITY_PROFILE);
    }
    this._profile = value;
  }

  get profile() {
    if (!this._profile) {
      this.profile = this.storage.getItem(StorageKeys.COMMUNITY_PROFILE);
    }
    return this._profile;
  }

  get showBetaType() {
    return this.betaCommunityService.showBetaType;
  }
  set showBetaType(value) {
    this.betaCommunityService.showBetaType = value;
  }

  get showBetaStatus() {
    return this.betaCommunityService.showBetaStatus;
  }
  set showBetaStatus(value) {
    this.betaCommunityService.showBetaStatus = value;
  }

  get showBetaReviewStatus() {
    return this.betaCommunityService.showBetaReviewStatus;
  }
  set showBetaReviewStatus(value) {
    this.betaCommunityService.showBetaReviewStatus = value;
  }

  get showBetaAuthor() {
    return this.betaCommunityService.showBetaAuthor;
  }
  set showBetaAuthor(value) {
    this.betaCommunityService.showBetaAuthor = value;
  }

  /**
   * Ordering for the NON-beta community feed: "recentactivity" or "date".
   *
   * Set by the channel component from the member's per-community preference before it loads
   * posts. Beta keeps its own `betaSortBy` (which has extra modes) — the two contexts never
   * share a feed, so they stay separate settings rather than one confused one.
   */
  public communitySortBy: "recentactivity" | "date" = "recentactivity";

  private _activeSortBy: string | null = null;

  /**
   * The ordering the feed CURRENTLY on screen uses, resolved from whichever of the two
   * settings above applies. Set once per load in `getPosts`, then read by
   * `sortActiveParentPosts`, which has no way to know which context it is sorting for.
   *
   * Falls back to `betaSortBy` while unset. Sorting is not only reached through a load —
   * the back-cache restores re-sort what they put back without going near `getPosts` — and
   * a plain field defaulting to "date" made those silently re-order a recent-activity feed
   * by creation date instead.
   */
  get activeSortBy(): string {
    return this._activeSortBy ?? this.betaSortBy;
  }
  set activeSortBy(value: string) {
    this._activeSortBy = value;
  }

  get betaSortBy() {
    return this.betaCommunityService.betaSortBy;
  }
  set betaSortBy(value) {
    this.betaCommunityService.betaSortBy = value;
  }

  get findJiraTicketNumber() {
    return this.betaCommunityService.findJiraTicketNumber;
  }
  set findJiraTicketNumber(value) {
    this.betaCommunityService.findJiraTicketNumber = value;
  }

  get betaFilterReadableValues() {
    return this.betaCommunityService.betaFilterReadableValues;
  }

  set betaFilterSettings(value) {
    this.betaCommunityService.betaFilterSettings = value;
  }

  get betaFilterSettings() {
    return this.betaCommunityService.betaFilterSettings;
  }

  private _connections: any;
  get connections() {
    if (!this._connections) this._connections = this.storage.getItem(StorageKeys.CONNECTIONS);
    return this._connections;
  }
  set connections(data) {
    if (data) {
      this._connections = data;
      this.storage.setItem(StorageKeys.CONNECTIONS, data);
    } else {
      this._connections = false;
      this.storage.removeItem(StorageKeys.CONNECTIONS);
    }
  }

  private urlRegex: RegExp = /(\bhttps?:\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;

  constructor(
    protected http: HttpClient,
    private storage: StorageService,
    private _win: WindowService,
    public helperService: HelperService,
    public userService: UserService
  ) {
    this.extraChannels.forEach((item) => {
      this.extraChannelsMap[item.id] = item;
    });
  }

  updateConnections(connections) {
    this.connectionRequests = undefined;
    connections.forEach((item) => {
      let theirID = item.initiated_by == this.userService.user.id ? item.requested_user_id : item.initiated_by;
      item.theirID = theirID;
      this.connectionMap[theirID] = item;
      if (item.requested_user_id == this.userService.user.id && item.status == "pending") {
        if (!this.connectionRequests) this.connectionRequests = [];
        this.connectionRequests.push(item);
      }
    });
    this.connections = connections;
  }

  getConnections(forceRefresh?) {
    const savedData = this.connections;
    if (savedData && !forceRefresh) {
      this.updateConnections(savedData);
      return observableOf(savedData);
    } else {
      return this.http.get(environment.paeApiUrl + "user/connections/", this.helperService.getHttpOptions()).pipe(
        map((result: any) => {
          this.updateConnections(result);
          return result;
        })
      );
    }
  }

  addConnection(userId, message = "") {
    const url = environment.paeApiUrl + "community/connection";

    const body = { message, userId };

    return this.http.post(url, JSON.stringify(body), this.helperService.getHttpOptions()).pipe(
      map((result: any) => {
        this.updateConnections(result.connections);
        let me = this.userService.user;
        me.connections = this.connections;
        this.userService.user = me;
        return result.connections;
      })
    );
  }

  removeConnection(connectionId, userId?) {
    const endpoint = this.http.delete(environment.paeApiUrl + `community/connection/${connectionId}`, this.helperService.getHttpOptions());
    return endpoint.pipe(
      map((result: any) => {
        this.updateConnections(result.connections);
        let me = this.userService.user;
        me.connections = this.connections;
        this.userService.user = me;
        return result.connections;
      })
    );
  }

  updateConnection(connection) {
    const endpoint = this.http.put(environment.paeApiUrl + `community/connection/${connection.id}`, JSON.stringify(connection), this.helperService.getHttpOptions());
    return endpoint.pipe(
      map((result: any) => {
        this.updateConnections(result.connections);
        let me = this.userService.user;
        me.connections = this.connections;
        this.userService.user = me;
        return result.connections;
      })
    );
  }

  // ---- Discord account linking (Phase 0) ----

  /**
   * Ask the backend to build the Discord authorize URL (with a signed `state`). The caller then
   * navigates the top-level window to that URL — a top-level redirect, so it cannot carry the
   * X-USER-TOKEN header, which is why the URL is fetched over an authenticated XHR first.
   */
  getDiscordAuthorizeUrl() {
    return this.http.get(environment.paeApiUrl + "oauth/discord/authorize", this.helperService.getHttpOptions());
  }

  /** Current member's Discord link status: `{ linked, link }`. */
  getDiscordLink() {
    return this.http.get(environment.paeApiUrl + "oauth/discord/link", this.helperService.getHttpOptions());
  }

  /** Unlink the member's Discord account. */
  unlinkDiscord() {
    return this.http.delete(environment.paeApiUrl + "oauth/discord/link", this.helperService.getHttpOptions());
  }

  /** Redeem the one-time Fender Studio Pro gift (backend enforces linked + guild-verified + once). */
  redeemDiscordGift() {
    return this.http.post(environment.paeApiUrl + "oauth/discord/redeem", JSON.stringify({}), this.helperService.getHttpOptions());
  }

  urlify(text) {
    if (text) {
      // Regex to match URLs
      var urlRegex = /(\bhttps?:\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;

      // Regex to check if the URL is already inside an <a> tag
      var htmlLinkRegex = /<a\s+[^>]*href=["']([^"']+)["'][^>]*>.*?<\/a>/gi;

      return text.replace(urlRegex, function (url) {
        // Check if the URL is inside an existing <a> tag
        if (htmlLinkRegex.test(text)) {
          // Reset the regex index since test() advances it
          htmlLinkRegex.lastIndex = 0;

          // If the URL is already inside an <a> tag, skip replacement
          if (text.match(htmlLinkRegex).some((link) => link.includes(url))) {
            return url;
          }
        }

        // Otherwise, convert the URL into an HTML link
        return '<a href="' + url + '" target="_blank">' + url + "</a>";
      });
    } else {
      return text;
    }
  }

  getProfileDetails(user_id) {
    if (this.cachedProfiles[user_id]) {
      return of(this.cachedProfiles[user_id]);
    } else {
      const url = (environment.features.usePaeapiForExchange ? environment.paeApiUrl : environment.apiUrl) + `exchange/creator/${user_id}`;
      return this.http.get(url, this.helperService.getHttpOptions(true, undefined, undefined)).pipe(
        map((data: any) => {
          this.cachedProfiles[user_id] = data;
          return data;
        })
      );
    }
  }

  getUserSkills(userId?, fresh?) {
    if (!userId) {
      if (!this.userService.user?.id) return observableOf([]);
      userId = this.userService.user.id;
    } else {
      fresh = true;
    }

    let userSkills = this.storage.getItem(StorageKeys.USER_SKILLS);
    if (userSkills && userSkills.length && !fresh && this.storage.checkTimestamp("user_skills")) {
      this.userHasSkills = true;
      userSkills.forEach((item) => {
        if (item.notes) {
          item.htmlNotes = this.urlify(item.notes);
        }
      });
      return observableOf(userSkills);
    } else {
      const url = environment.paeApiUrl + `community/user/${userId}/skills`;
      return this.http.get(url, this.helperService.getHttpOptions()).pipe(
        map((data: any) => {
          if (data?.skills?.length) {
            this.userHasSkills = true;
            this.storage.setTimestamp("user_skills", 24);
            this.storage.setItem(StorageKeys.USER_SKILLS, data.skills);
            data.skills.forEach((item) => {
              if (item.notes) {
                item.htmlNotes = this.urlify(item.notes);
              }
            });
          }
          return data?.skills || [];
        })
      );
    }
  }

  addUserSkills(args) {
    //to do: need to sync the query map in the web communtiy service.
    const endpoint = this.http.put(environment.paeApiUrl + `community/user/skills`, JSON.stringify(args), this.helperService.getHttpOptions());
    return endpoint.pipe();
  }

  getAvailableSkills(fresh = true) {
    let availableSkills = this.storage.getItem(StorageKeys.AVAILABLE_SKILLS);

    if (!fresh && availableSkills && availableSkills.length) {
      this.availableSkills = availableSkills;
      this.availableSkills.sort((a, b) => (a.title > b.title ? 1 : -1));
      availableSkills.forEach((item) => (this.skillsMap[item.string_id] = item));
      this.taggableChannels = [...this.availableSkills, ...this.extraChannels.filter((item) => item.taggable)];
      // make sure availableSkills and taggableChannels has only unqiue values, as we may have accidentally saved the extraChannels to local storage.
      if (availableSkills && availableSkills.length) {
        const uniqueSkills = Array.from(new Set(availableSkills.map((item) => item.id))).map((id) => availableSkills.find((item) => item.id === id));
        availableSkills = uniqueSkills;
      }
      if (this.taggableChannels && this.taggableChannels.length) {
        const uniqueTaggableChannels = Array.from(new Set(this.taggableChannels.map((item) => item.id))).map((id) => this.taggableChannels.find((item) => item.id === id));
        this.taggableChannels = uniqueTaggableChannels;
      }
      this.taggableChannels.sort((a, b) => (a.title > b.title ? 1 : -1));
      this.taggableChannels.forEach((item) => (this.taggableChannelsMap[item.string_id] = item));
      return observableOf(availableSkills);
    } else {
      const url = environment.paeApiUrl + "community/skills/";
      return this.http.get(url, this.helperService.getHttpOptions()).pipe(
        map((data: any) => {
          this.availableSkills = data.skills;
          this.availableSkills.sort((a, b) => {
            return a.title > b.title ? 1 : -1;
          });
          this.taggableChannels = [...this.availableSkills, ...this.extraChannels.filter((item) => item.taggable)];
          this.taggableChannels.sort((a, b) => (a.title > b.title ? 1 : -1));
          this.taggableChannels.forEach((item) => (this.taggableChannelsMap[item.string_id] = item));
          data.skills.forEach((item) => (this.skillsMap[item.string_id] = item));
          this.storage.setItem(StorageKeys.AVAILABLE_SKILLS, this.availableSkills);
          return data.skills;
        })
      );
    }
  }

  adminAddSkill(args) {
    const url = environment.paeApiUrl + "community/skill";
    return this.http.post(url, JSON.stringify(args), this.helperService.getHttpOptions()).pipe(
      map((result: any) => {
        return result;
      })
    );
  }

  adminDeleteSkill(id) {
    return this.http.delete(environment.paeApiUrl + `community/skill/${id}`, this.helperService.getHttpOptions()).pipe();
  }

  updateShowcaseSkill(args) {
    const url = environment.paeApiUrl + "community/showcase";
    return this.http.post(url, JSON.stringify(args), this.helperService.getHttpOptions()).pipe();
  }

  deleteShowcaseSkill(showcaseId, skillId) {
    const url = environment.paeApiUrl + `community/showcase/${showcaseId}/skill/${skillId}`;
    return this.http.delete(url, this.helperService.getHttpOptions()).pipe();
  }

  getShowcases(userId?) {
    const url = environment.paeApiUrl + (userId ? `community/user/${userId}/showcases/` : `community/showcases`);
    return this.http.get(url, this.helperService.getHttpOptions()).pipe(
      map((data: any) => {
        if (data.showcases && data.showcases.length) {
          data.showcases.forEach((item) => {
            item.url = this.buildFileUrl(item.user_id, item);
          });
        }
        return data.showcases;
      })
    );
  }

  buildFileUrl(user_id, file) {
    let url;
    if (file.url) url = file.url;
    if (file.type == "post_attachment") {
      url = (file.storage_location == "aws" ? environment.awsContentUrl : environment.wasabiContentUrl) + user_id + "/post-attachments/" + encodeURI(file.storage_id ? file.storage_id : file.filename);
    } else if (file.type == "thread_attachment") {
      url =
        (file.storage_location == "aws" ? environment.awsContentUrl : environment.wasabiContentUrl) +
        user_id +
        "/thread-attachments/" +
        file.thread_id +
        "/" +
        (file.storage_id ? file.storage_id : file.filename);
    } else if (file.type == "showcase") {
      url = (file.storage_location == "aws" ? environment.awsContentUrl : environment.wasabiContentUrl) + user_id + "/showcases/" + encodeURI(file.storage_id ? file.storage_id : file.filename);
    } else if (file.workspace_id) {
      url =
        (file.storage_location == "aws" ? environment.awsContentUrl : environment.wasabiContentUrl) +
        user_id +
        "/workspace-uploads/" +
        file.workspace_id +
        "/" +
        (file.storage_id ? file.storage_id : file.filename);
    }
    return url;
  }

  clearCache() {
    this.connections = [];
    this.profile = undefined;
  }

  public addingPost: any;
  addPost(data, collectionReference): any {
    // parentID must exist for firebase to index it (for finding parent posts)
    if (!data.parentID && data.parentID !== null) data.parentID = null;
    data.type = data.parentID ? "reply" : "post";
    for (var i in data) {
      if (data[i] === undefined) delete data[i];
    }
    if (Array.isArray(data.mentions)) data.mention_ids = mentionIdsOf(data.mentions);
    delete data.iLike;
    this.addingPost = data;
    return new Promise((resolve, reject) => {
      collectionReference
        .add(data)
        .then((result) => {
          resolve(result);
        })
        .catch((err) => reject(err));
    });
  }

  findMembersBySkills(skills, limit = 2000, collectionRef) {
    return collectionRef
      .where("skillsQueryMap", "array-contains-any", skills)
      .where("id", "!=", null)
      .where("public", "==", true)
      .limit(limit)
      .get()
      .then((result: any) => {
        let profiles = [];
        result.docs.forEach((item) => {
          let data = item.data();
          let skillsString = "";
          if (data.skillsQueryMap && data.skillsQueryMap.length) {
            // `skillsMap[string_id].title` is a per-locale map since the DENG-728 channel
            // migration (`{ en: "Mixing", es: "Mezcla", ... }`). Concatenating the object
            // directly renders `[object Object]` — resolve to the current language first.
            const lang = this.languageService.selectedLanguage?.code || "en";
            data.skillsQueryMap.forEach((string_id, index) => {
              const title = resolveLocalizedString(this.skillsMap[string_id]?.title, lang, string_id);
              skillsString += title + (index == data.skillsQueryMap.length - 1 ? "" : ", ");
            });
          }
          profiles.push({
            ...data,
            skills_string: skillsString,
          });
        });
        return profiles;
      });
  }

  findMembersByName(skills, collectionRef) {
    return collectionRef
      .where("name", "array-contains-any", skills)
      .where("public", "==", true)
      .get()
      .then((result: any) => {
        let profiles = [];
        result.docs.forEach((item) => {
          profiles.push(item.data());
        });
        return profiles;
      });
  }

  findModeratedUsers(collectionRef) {
    return collectionRef
      .where("status", "in", ["probation", "banned"])
      .get()
      .then((result: any) => {
        let profiles = [];
        result.docs.forEach((item) => {
          profiles.push(item.data());
        });
        return profiles;
      });
  }

  findMembers(collectionRef, skills?, nameQuery?, emailQuery?, limit = 5, includePsuedoExperts?) {
    // Results are collected into separate buckets and merged in priority order
    // at the end, rather than into one shared array as each query resolves.
    // Ordering used to depend on which Firestore round trip came back first,
    // which matters a lot now that the mention picker only shows five rows.
    const byName = [];
    const byLastName = [];
    const byEmail = [];
    const bySkills = [];
    const queries = [];

    const collect = (bucket) => (result) => {
      result.docs.forEach((item: any) => bucket.push(item.data()));
    };

    // A missing composite index rejects. Fail that one query soft so it can
    // never take down the rest of the search -- notably, the lastName_lowercase
    // index has to be created in the Firebase console before it starts working.
    const onQueryError = (label) => (err) => {
      console.warn("findMembers: " + label + " query failed", err);
    };

    const prefixQuery = (field, value) =>
      collectionRef
        .where(field, ">=", value.toLowerCase())
        .where(field, "<=", value.toLowerCase() + "~")
        // Hidden profiles are excluded everywhere, @mention search included:
        // opting out of a visible profile opts you out of being found.
        .where("public", "==", true)
        .limit(limit)
        .get();

    if (skills && skills.length) {
      queries.push(collectionRef.where("skillsQueryMap", "array-contains-any", skills).where("public", "==", true).limit(limit).get().then(collect(bySkills)).catch(onQueryError("skills")));
    }
    if (nameQuery) {
      queries.push(prefixQuery("name_lowercase", nameQuery).then(collect(byName)).catch(onQueryError("name_lowercase")));
      // People type full names, so "@Coffin" has to reach Dave Coffin. Without
      // this the only way to mention someone is to start with their first name.
      queries.push(prefixQuery("lastName_lowercase", nameQuery).then(collect(byLastName)).catch(onQueryError("lastName_lowercase")));
    }
    if (emailQuery) {
      // This query used to be fired without being pushed onto `queries`, so its
      // results landed after the caller had already been handed the array.
      queries.push(prefixQuery("email", emailQuery).then(collect(byEmail)).catch(onQueryError("email")));
    }

    return Promise.all(queries).then(() => {
      const map = {};
      const allResults = [];
      // Full-name hits outrank last-name hits, which outrank email and skills.
      [byName, byLastName, byEmail, bySkills].forEach((bucket) => {
        bucket.forEach((profile: any) => {
          if (profile && !map[profile.id]) {
            map[profile.id] = true;
            allResults.push(profile);
          }
        });
      });
      if (includePsuedoExperts && nameQuery && "studio one experts".indexOf(nameQuery.toLowerCase()) >= 0) {
        allResults.unshift({
          description: null,
          email: null,
          id: "studio_one_experts",
          firstName: "Studio One",
          firstName_lowercase: "studio one",
          lastName: "Experts",
          lastName_lowercase: "experts",
          name: "Studio One Experts",
          name_lowercase: "studio one experts",
          photo: "./assets/images/s16-6.png",
          public: true,
        });
      }
      return allResults;
    });
  }

  public unreadNotificationCount: number = 0;
  public notificationCount: number = 0;
  getUnreadNotificationCount(collectionRef) {
    return collectionRef
      .get()
      .then((result) => {
        this.notificationCount = result.size;
        this.unreadNotificationCount = 0;
        result.docs.forEach((doc) => {
          let data = doc.data();
          if (!data.seen) this.unreadNotificationCount++;
        });
      })
      .catch(() => {});
  }

  getNotifications(collectionRef) {
    return collectionRef
      .get()
      .then((result) => {
        if (result.empty) {
          return [];
        } else {
          let docs = [];
          result.docs.forEach((doc) => {
            let data: any = doc.data();
            data.id = doc.id;
            docs.push(data);
          });
          docs.sort((a, b) => {
            if (a.created && b.created) {
              return a.created.toDate() > b.created.toDate() ? -1 : 1;
            } else {
              return 1;
            }
          });
          return docs;
        }
      })
      .catch(() => {});
  }

  deleteNotification(documentRef) {
    return documentRef.delete();
  }

  batchDeleteNotifications(collectionRef, ids: Array<string>) {}

  updateNotification(documentRef, data) {
    return documentRef.update(data);
  }

  updatePost(documentReference, args) {
    // Keep `mention_ids` in step whenever an edit rewrites `mentions` (null clears both).
    if (args && Object.prototype.hasOwnProperty.call(args, "mentions")) {
      args = { ...args, mention_ids: mentionIdsOf(args.mentions) };
    }
    return documentReference.update(args);
  }

  deletePost(id, collectionRef, admin?) {
    let args: any = {
      deleted: {
        timestamp: new Date(),
        deleted_by: this.userService.user.id,
      },
    };
    if (admin) args.deleted.admin = true;
    return collectionRef.doc(id).update(args);
  }

  createConnectedUserMessageThread(collectionRef, user_id) {
    return new Promise(async (resolve, reject) => {
      let threadExists = false;
      // Firebase only allows one array-contains clause per query, so we search
      // all the current user's threads for one containing the provided user_id.
      const threads = await this.getConnectedUserMessageThreads(collectionRef);
      threads.forEach(({ users }) => {
        if (users.includes(this.userService.user.id) && users.includes(user_id)) {
          threadExists = true;
        }
      });

      if (threadExists) {
        reject(new Error("This thread already exists."));
      } else {
        collectionRef
          .add({
            users: [this.userService.user.id, user_id],
            created: new Date(),
          })
          .then(() => {
            resolve(true);
          });
      }
    });
  }

  async getConnectedUserMessageThreads(collectionRef) {
    const userIdString = this.userService.user.id.toString();
    const userIdNumber = Number(this.userService.user.id);
    const result = await collectionRef.where("users", "array-contains-any", [userIdString, userIdNumber]).get();
    const threads = [];
    if (!result.empty) {
      await Promise.all(
        result.docs.map(async (thread) => {
          const data = thread.data();

          // Check if any user IDs are numbers and convert them to strings
          if (data.users.some((id) => typeof id === "number")) {
            const updatedUsers = data.users.map((id) => id.toString());
            // Update the document with string IDs
            await thread.ref.update({ users: updatedUsers });
            data.users = updatedUsers;
          }

          data.them = data.users.find((id) => id != this.userService.user.id);
          data.id = thread.id;
          data.connection = this.connectionMap[data.them];
          data.latestMessage = null;
          // Fetch the latest message from the messages subcollection
          try {
            const messagesRef = thread.ref.collection("messages");
            const latestMessageSnapshot = await messagesRef.orderBy("timestamp", "desc").limit(1).get();
            if (!latestMessageSnapshot.empty) {
              data.latestMessage = latestMessageSnapshot.docs[0].data();
            } else {
              data.latestMessage = null;
            }
          } catch (error) {}
          threads.push(data);
        })
      );
    }
    return threads;
  }

  getPostByID(id, collectionRef) {
    return new Promise((resolve, reject) => {
      if (!this.activeParentThreadMap) this.activeParentThreadMap = {};

      collectionRef
        .doc(id)
        .get()
        .then((result) => {
          let promises = [];
          let post: any = result.data();
          if (post && !post.deleted) {
            post.id = result.id;
            post.key = result.id;
            if (post.beta_string_id && !post.type) {
              post.type = post.parentID ? "reply" : "post";
              collectionRef.doc(result.id).update({ type: post.type });
            }
            this.buildHtmlComment(post);
            promises.push(
              collectionRef
                .where("parentID", "==", result.id)
                .get()
                .then((replies) => {
                  this.activeParentThreadMap[id] = [];
                  replies.docs.forEach((replyDoc) => {
                    let reply: any = replyDoc.data();
                    reply.key = replyDoc.id;
                    reply.id = replyDoc.id;
                    if ((post.beta_string_id || reply.beta_string_id) && !reply.type) {
                      reply.type = "reply";
                      collectionRef.doc(replyDoc.id).update({ type: reply.type });
                    }
                    this.buildHtmlComment(reply);
                    if (this._win.location.host == "nativescript" && reply.created && reply.created.seconds) reply.created = moment.unix(reply.created.seconds).toDate();
                    this.activeParentThreadMap[id].push(reply);
                  });
                  this.activeParentThreadMap[id].sort((a, b) => {
                    return a.created > b.created ? 1 : -1;
                  });
                })
            );
            return Promise.all(promises).then((result) => {
              resolve(post);
            });
          } else {
            reject(new Error("Post not found!"));
          }
        });
    });
  }

  /** Firestore caps `in` at 30 values. */
  private static readonly REPLY_BATCH = 30;

  /**
   * The non-deleted replies to `parents`, for a feed page.
   *
   * Batched: one `parentID in [...30]` query per 30 threads, where it used to be one query per
   * thread — 25 round trips, with the feed waiting on the slowest.
   *
   * Skipped entirely, per thread, when `deferRepliesInFeed` is on and the parent carries its
   * own `reply_count`. Those parents are flagged `_repliesDeferred` so anything that opens the
   * thread knows the feed's (empty) reply list is not the real one and fetches it.
   *
   * `backfillType` writes `type: "reply"` onto legacy replies that lack it, as both feed paths
   * always have.
   */
  loadFeedReplies(parents: Array<any>, collectionReference: any, backfillType: (reply: any) => boolean): Promise<Array<any>> {
    const needed: string[] = [];
    (parents || []).forEach((post) => {
      if (!post?.id) return;
      if (this.deferRepliesInFeed && typeof post.reply_count === "number") {
        post._repliesDeferred = true;
        return;
      }
      delete post._repliesDeferred;
      needed.push(String(post.id));
    });
    const ids = Array.from(new Set(needed));
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += CommunityService.REPLY_BATCH) chunks.push(ids.slice(i, i + CommunityService.REPLY_BATCH));
    return Promise.all(
      chunks.map((chunk) =>
        collectionReference
          .where("parentID", "in", chunk)
          .get()
          .then((snapshot: any) => {
            const replies: any[] = [];
            snapshot.docs.forEach((replyDoc: any) => {
              const reply: any = replyDoc.data();
              if (reply.deleted) return;
              reply.key = replyDoc.id;
              reply.id = replyDoc.id;
              if (!reply.type && backfillType(reply)) {
                reply.type = "reply";
                collectionReference.doc(replyDoc.id).update({ type: reply.type });
              }
              replies.push(reply);
            });
            return replies;
          })
      )
    ).then((lists: any[][]) => lists.flat());
  }

  getPostsWithReplies(
    args: {
      channel?: any;
      topic?: any;
      postsAccountID?: string;
      moderationAccountID?: string;
      pins?: boolean;
      mostPopular?: boolean;
      limit?: number;
      storageKey?: string;
      clear?: boolean;
      id?: string;
      betaStringId?: string;
      /**
       * When set (non-beta paths only), scopes the query to posts tagged with this
       * `community_string_id`. Firestore will require a new composite index the first
       * time a filtered branch runs — the console error links straight to a "create index"
       * button. Not applied to postsAccountID/moderationAccountID/id branches: those are
       * user-profile and moderation views that legitimately span communities.
       */
      communityStringId?: string;
    },
    collectionReference
  ) {
    if (!args.limit) args.limit = 30;
    if (args.clear) {
      this.lastVisiblePost = undefined;
      this.lastVisibleRecentReply = undefined;
    }

    // Beta and public communities each have their own ordering setting; whichever feed is
    // being loaded decides, and the answer is parked for `sortActiveParentPosts` to read.
    this.activeSortBy = args.betaStringId ? this.betaSortBy : this.communitySortBy;

    // Recent-activity ordering is served from Firestore, fanning in from the replies
    // collection: the Algolia index carries no last-reply timestamp to sort on. The excluded
    // cases are views that are not "a channel of threads" — a single post, one member's
    // posts, a topic, pins, or most-popular — where "newest reply" is not the question.
    const wantsRecentReplies = this.activeSortBy == "recentactivity" && (args.betaStringId || args.communityStringId);
    // My Posts and My Pinned Posts are excluded for the same reason `args.postsAccountID`
    // and `args.pins` already are — they are collections of YOUR threads, not a channel of
    // conversation, and the reply scan cannot express either: a reply carries nothing about
    // its parent's author, and `pins` lives on the parent. So the scan runs community-wide
    // and the ownership test happens client-side, which means every page is discarded and
    // the "all filtered out, fetch the next page" retry walks the whole community. That is
    // the spinner that never stops. The normal path below queries both correctly
    // (`author.id ==` / `pins array-contains`), so send them there.
    const ownershipView = args.channel?.string_id === "my_posts" || args.channel?.string_id === "pinned_posts";
    if (wantsRecentReplies && !ownershipView && !args.moderationAccountID && !args.postsAccountID && !args.topic && !args.pins && !args.mostPopular) {
      return this.getPostsByRecentReplies(args, collectionReference);
    }

    return new Promise((resolve, reject) => {
      let posts = [];
      let method = collectionReference;
      if (args.id) {
        method = method.where("parentID", "==", null).where("author.id", "in", [args.postsAccountID.toString(), Number(args.postsAccountID)]);
      } else if (args.postsAccountID) {
        method = method.where("parentID", "==", null).where("author.id", "in", [args.postsAccountID.toString(), Number(args.postsAccountID)]);
      } else if (args.moderationAccountID) {
        method = method.where("author.id", "in", [args.moderationAccountID.toString(), Number(args.moderationAccountID)]);
      } else if (args.topic) {
        method = method.where("parentID", "==", null).where("topics", "array-contains", args.topic.string_id);
        if (args.communityStringId) method = method.where("community_string_id", "==", args.communityStringId);
      } else if (args.channel && args.channel.string_id != "all_posts" && args.channel.string_id != "pinned_posts" && args.channel.string_id != "my_posts") {
        if (args.betaStringId) {
          method = method.where("parentID", "==", null).where("channels", "array-contains", args.channel.string_id).where("beta_string_id", "==", args.betaStringId);
          if (this.findJiraTicketNumber) {
            method = method.where("beta_review_details.jira_ticket_number", "==", this.findJiraTicketNumber);
          } else {
            if (this.showBetaType) method = method.where("beta_type", "==", this.showBetaType);
            if (this.showBetaStatus) method = method.where("beta_status", "==", this.showBetaStatus);
            if (this.showBetaReviewStatus) method = method.where("reviewed", "==", this.showBetaReviewStatus == "true");
            if (this.showBetaAuthor) method = method.where("author.id", "==", this.showBetaAuthor);
          }
        } else {
          method = method.where("parentID", "==", null).where("channels", "array-contains", args.channel.string_id);
          if (args.communityStringId) method = method.where("community_string_id", "==", args.communityStringId);
        }
      } else if (args.mostPopular) {
        method = method.where("parentID", "==", null).where("likeCount", ">", 4);
        if (args.communityStringId) method = method.where("community_string_id", "==", args.communityStringId);
      } else if (args.channel && args.channel.string_id == "my_posts") {
        if (!this.userService.user?.id) {
          this.processPostsAndReplies([], args.clear, args.storageKey, undefined, args.betaStringId);
          resolve([]);
          return;
        }
        if (args.betaStringId) {
          method = method.where("parentID", "==", null).where("beta_string_id", "==", args.betaStringId).where("author.id", "==", this.userService.user.id);
          if (this.findJiraTicketNumber) {
            method = method.where("beta_review_details.jira_ticket_number", "==", this.findJiraTicketNumber);
          } else {
            if (this.showBetaType) method = method.where("beta_type", "==", this.showBetaType);
            if (this.showBetaStatus) method = method.where("beta_status", "==", this.showBetaStatus);
            if (this.showBetaReviewStatus) method = method.where("reviewed", "==", this.showBetaReviewStatus == "true");
          }
        } else {
          method = method.where("parentID", "==", null).where("author.id", "==", this.userService.user.id);
          if (args.communityStringId) method = method.where("community_string_id", "==", args.communityStringId);
        }
      } else if ((args.channel && args.channel.string_id == "pinned_posts") || args.pins) {
        if (!this.userService.user?.id) {
          this.processPostsAndReplies([], args.clear, args.storageKey, undefined, args.betaStringId);
          resolve([]);
          return;
        }
        method = method.where("parentID", "==", null).where("pins", "array-contains", this.userService.user.id);
        if (args.communityStringId) method = method.where("community_string_id", "==", args.communityStringId);
      } else {
        if (args.betaStringId) {
          method = method.where("parentID", "==", null).where("beta_string_id", "==", args.betaStringId);
          if (this.findJiraTicketNumber) {
            method = method.where("beta_review_details.jira_ticket_number", "==", this.findJiraTicketNumber);
          } else {
            if (this.showBetaType) method = method.where("beta_type", "==", this.showBetaType);
            if (this.showBetaStatus) method = method.where("beta_status", "==", this.showBetaStatus);
            if (this.showBetaReviewStatus) method = method.where("reviewed", "==", this.showBetaReviewStatus == "true");
            if (this.showBetaAuthor) method = method.where("author.id", "==", this.showBetaAuthor);
          }
        } else {
          method = method.where("parentID", "==", null);
          if (args.communityStringId) method = method.where("community_string_id", "==", args.communityStringId);
        }
      }
      let postsPromise;
      if (this.lastVisiblePost && !args.storageKey && !args.mostPopular) {
        postsPromise = method.orderBy("created", "desc").startAfter(this.lastVisiblePost).limit(args.limit).get();
      } else if (args.storageKey && !args.clear && this.lastVisiblePostsByKey[args.storageKey]) {
        postsPromise = method.orderBy("created", "desc").startAfter(this.lastVisiblePostsByKey[args.storageKey]).limit(args.limit).get();
      } else {
        if (args.mostPopular) {
          postsPromise = method.orderBy("likeCount", "desc").limit(20).get();
        } else {
          postsPromise = method.orderBy("created", "desc").limit(args.limit).get();
        }
      }

      postsPromise
        .then((result) => {
          let promises = [];
          this.lastVisiblePost = result.docs[result.docs.length - 1];
          if (args.storageKey) {
            this.lastVisiblePostsByKey[args.storageKey] = result.docs[result.docs.length - 1];
          }
          if (result.docs && result.docs.length) {
            this.activePostsLimitReached = result.docs.length < args.limit ? true : false;
            result.docs.forEach((doc) => {
              let post: any = doc.data();
              post.key = doc.id;
              post.id = doc.id;
              if ((args.betaStringId || post.beta_string_id) && !post.type) {
                post.type = post.parentID ? "reply" : "post";
                collectionReference.doc(doc.id).update({ type: post.type });
              }
              if (post.likes && post.likes.length && this.userService.user?.id) post.iLike = post.likes.filter((id) => id == this.userService.user.id).length;
              if (args.moderationAccountID) {
                posts.push(post); // all posts should be returned for moderation
              } else if (!this.userService.user?.active_subscription && post.sphere_only) {
                // dont process this post because its sphere only and this user doesn't have an active subscription.
              } else if (!this.userService.isEmployee && post.employee_only) {
                // dont process this post because its employee only and this user is not an employee.
              } else if (args.betaStringId && post.pinned) {
                // dont process this post because its pinned and this is a beta community.
              } else if (post.deleted) {
                // dont process this post because its deleted.
              } else if (post.scheduled_date) {
                post.created = post.scheduled_date;
                if (new Date() > new Date(post.scheduled_date.seconds * 1000)) {
                  delete post.scheduled_date;
                }
                posts.push(post);
              } else if (!args.channel || this.getChannelId(args.channel) === "all_posts") {
                if (args.postsAccountID) {
                  if (post.topics && post.topics.length) {
                    // dont add it if its in a topic.
                  } else {
                    posts.push(post);
                  }
                } else if (args.channel && this.getChannelId(args.channel) === "all_posts") {
                  if ((post.channels && post.channels.includes("feedback")) || (!args.topic && post.topics && post.topics.length)) {
                    // includes feedback so ignore it.
                    // or was posted in a topic, it should not show in all posts.
                  } else {
                    posts.push(post);
                  }
                } else if (args.pins || args.topic) {
                  posts.push(post);
                }
              } else {
                posts.push(post);
              }
            });
            if (posts.length) {
              if (!args.moderationAccountID) {
                promises.push(
                  this.loadFeedReplies(posts, collectionReference, (reply) => !!(args.betaStringId || reply.beta_string_id)).then((replies) => {
                    posts.push(...replies);
                  })
                );
              }
              return Promise.all(promises).then((result) => {
                // Get channel ID for filtering
                const channelId = args.channel ? args.channel.string_id || args.channel.id : undefined;
                this.processPostsAndReplies(posts, args.clear, args.storageKey, channelId, args.betaStringId);
                resolve(posts);
              });
            } else {
              const channelId = args.channel ? args.channel.string_id || args.channel.id : undefined;
              this.processPostsAndReplies([], args.clear, args.storageKey, channelId, args.betaStringId);
              resolve([]);
            }
          } else {
            this.activePostsLimitReached = true;
            const channelId = args.channel ? args.channel.string_id || args.channel.id : undefined;
            this.processPostsAndReplies([], args.clear, args.storageKey, channelId, args.betaStringId);
            resolve([]);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getPostsByRecentReplies(
    args: {
      channel?: any;
      topic?: any;
      postsAccountID?: string;
      moderationAccountID?: string;
      pins?: boolean;
      mostPopular?: boolean;
      limit?: number;
      storageKey?: string;
      clear?: boolean;
      id?: string;
      betaStringId?: string;
      communityStringId?: string;
    },
    collectionReference
  ) {
    return new Promise((resolve, reject) => {
      const pageLimit = 25;

      // Scope either feed to "here". Beta and public communities hold the same shape of
      // content; only the field identifying the community differs.
      //
      // The CHANNEL has to be in the query too, not just in the filter below. Scanning the
      // whole community and discarding the misses means a quiet channel yields nothing per
      // page, so the "everything was filtered out, fetch the next page" retry walks the
      // entire community a page at a time — seconds per page, the feed stuck on one post,
      // and a stream of Firestore reads that looks like a runaway loop. Replies carry their
      // parent's `channels`, so the same predicate scopes both streams.
      const channelId = args.channel && args.channel.string_id != "all_posts" && args.channel.string_id != "pinned_posts" && args.channel.string_id != "my_posts" ? args.channel.string_id : null;
      const scope = (method: any) => {
        let scoped = args.betaStringId ? method.where("beta_string_id", "==", args.betaStringId) : method.where("community_string_id", "==", args.communityStringId);
        if (channelId) scoped = scoped.where("channels", "array-contains", channelId);
        return scoped;
      };

      /** Shape a raw doc into a feed post, back-filling `type` the way this path always has. */
      const toParent = (doc: any) => {
        const parent: any = doc.data();
        parent.key = doc.id;
        parent.id = doc.id;
        if (!parent.type) {
          parent.type = "post";
          collectionReference.doc(doc.id).update({ type: parent.type });
        }
        if (parent.likes && parent.likes.length && this.userService.user?.id) {
          parent.iLike = parent.likes.filter((id: string) => id == this.userService.user.id).length;
        }
        return parent;
      };

      // TWO streams, unioned.
      //
      // Fanning in from replies alone is what orders the feed by newest answer — but a thread
      // nobody has replied to has no reply to be found by, so it never appeared at all. On
      // beta that was survivable; as the default ordering for a public community it means you
      // post something and watch it vanish on refresh.
      //
      // So: recent replies (for threads brought back to life) UNION recent threads (for
      // everything new), sorted by activity below. Each stream keeps its own cursor, and
      // paging advances both.
      const replyCursorBeforeFetch = this.lastVisibleRecentReply;
      let replyQuery: any = scope(collectionReference.where("type", "==", "reply")).orderBy("created", "desc");
      if (replyCursorBeforeFetch) replyQuery = replyQuery.startAfter(replyCursorBeforeFetch);

      const postCursorBeforeFetch = this.lastVisiblePost;
      let postQuery: any = scope(collectionReference.where("parentID", "==", null)).orderBy("created", "desc");
      if (postCursorBeforeFetch) postQuery = postQuery.startAfter(postCursorBeforeFetch);

      Promise.all([replyQuery.limit(pageLimit).get(), postQuery.limit(pageLimit).get()])
        // A rejected query — most likely a Firestore composite index that does not exist in
        // this environment yet — must not leave the feed half-initialised. Resolve to an
        // empty feed so the UI settles, and re-throw nothing: the console already carries
        // Firestore's own error, which names the missing index and links to its creation.
        .catch((error: any) => {
          console.error("getPostsByRecentReplies query failed", error);
          return [{ docs: [] }, { docs: [] }];
        })
        .then(([repliesResult, postsResult]: any[]) => {
          const replyDocs = repliesResult?.docs || [];
          const postDocs = postsResult?.docs || [];

          if (!replyDocs.length && !postDocs.length) {
            this.activePostsLimitReached = true;
            if (!replyCursorBeforeFetch && !postCursorBeforeFetch) {
              this.processPostsAndReplies([], args.clear, args.storageKey);
            }
            resolve([]);
            return;
          }

          if (replyDocs.length) this.lastVisibleRecentReply = replyDocs[replyDocs.length - 1];
          if (postDocs.length) this.lastVisiblePost = postDocs[postDocs.length - 1];
          // Exhausted only when BOTH streams are — one running dry does not mean the feed is.
          this.activePostsLimitReached = replyDocs.length < pageLimit && postDocs.length < pageLimit;

          let parentIDs = new Set<string>();
          let replies: any[] = [];

          replyDocs.forEach((replyDoc: any) => {
            let reply: any = replyDoc.data();
            if (!reply.deleted && reply.parentID) {
              reply.key = replyDoc.id;
              reply.id = replyDoc.id;
              if (!reply.type) {
                reply.type = "reply";
                collectionReference.doc(replyDoc.id).update({ type: reply.type });
              }
              parentIDs.add(reply.parentID);
              replies.push(reply);
            }
          });

          // Threads from the post stream are already in hand; only the parents named by a
          // reply and not among them still need reading.
          const directParents = postDocs.map(toParent);
          const directIds = new Set<string>(directParents.map((post: any) => String(post.id)));

          if (!parentIDs.size && !directParents.length) {
            this.processPostsAndReplies([], args.clear, args.storageKey);
            resolve([]);
            return;
          }

          let parentIDsArray = Array.from(parentIDs).filter((parentID) => !directIds.has(String(parentID)));
          let parentPromises = parentIDsArray.map((parentID) => {
            return collectionReference
              .doc(parentID)
              .get()
              .then((parentDoc) => (parentDoc.exists ? toParent(parentDoc) : null));
          });

          Promise.all(parentPromises)
            .then((fetchedParents) => {
              const parentPosts = [...directParents, ...fetchedParents];
              const validParentPosts = parentPosts.filter((post) => {
                if (!post) return false;

                // `post.pinned` is excluded for BETA only: beta renders pinned posts in its
                // own announcements section above the feed, so leaving them in would show
                // them twice. Public communities have no such section and the Algolia path
                // does not drop them, so dropping them here would make a thread vanish the
                // moment it was pinned.
                if ((!this.userService.user?.active_subscription && post.sphere_only) || (args.betaStringId && post.pinned) || (!this.userService.isEmployee && post.employee_only) || post.deleted) {
                  return false;
                }

                if (args.channel && args.channel.string_id == "my_posts") {
                  if (!post.author || post.author.id != this.userService.user?.id) return false;
                } else if (args.channel && args.channel.string_id != "all_posts" && args.channel.string_id != "pinned_posts") {
                  if (!post.channels || !post.channels.includes(args.channel.string_id)) return false;
                }

                if (this.findJiraTicketNumber) {
                  if (!post.beta_review_details || post.beta_review_details.jira_ticket_number != this.findJiraTicketNumber) return false;
                } else {
                  if (
                    (this.showBetaType && post.beta_type != this.showBetaType) ||
                    (this.showBetaStatus && post.beta_status != this.showBetaStatus) ||
                    (this.showBetaReviewStatus !== undefined && post.reviewed != (this.showBetaReviewStatus == "true")) ||
                    (this.showBetaAuthor && post.author && post.author.id != this.showBetaAuthor)
                  ) {
                    return false;
                  }
                }

                return true;
              });

              if (validParentPosts.length === 0) {
                if (replyDocs.length === pageLimit || postDocs.length === pageLimit) {
                  // All posts in this batch were filtered out but more data exists — fetch the next page
                  // to avoid permanently skipping content behind a cursor gap.
                  resolve(this.getPostsByRecentReplies(args, collectionReference));
                } else {
                  this.processPostsAndReplies([], args.clear, args.storageKey);
                  resolve([]);
                }
                return;
              }

              this.loadFeedReplies(validParentPosts, collectionReference, () => true)
                .then((replies) => {
                  const allPosts: any[] = [...validParentPosts, ...replies];
                  this.processPostsAndReplies(allPosts, args.clear, args.storageKey);
                  resolve(allPosts);
                })
                .catch((error) => {
                  reject(error);
                });
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  addSoftReply(data, post) {
    const parentId = post?.id || post?.key;
    if (!parentId) return;
    if (!this.activeParentThreadMap[parentId]) this.activeParentThreadMap[parentId] = [];
    if (!this.activeParentThreadMapTruncated[parentId]) this.activeParentThreadMapTruncated[parentId] = [];
    const replyId = data?.id || data?.key;
    const alreadyAdded = replyId && this.activeParentThreadMap[parentId].some((r) => r.id === replyId || r.key === replyId);
    if (!alreadyAdded) {
      this.activeParentThreadMap[parentId].push(data);
      this.activeParentThreadMapTruncated[parentId].push(data);
    }
    if (this.displayedPostsMap && replyId) {
      this.displayedPostsMap[replyId] = data;
    }
  }

  /**
   * Sort activeParentPosts according to the ordering the current feed uses —
   * `activeSortBy`, resolved in `getPosts` from the beta or community setting.
   * Used by processPostsAndReplies and by live watchers so Recent Replies order is not overwritten.
   */
  sortActiveParentPosts(options?: { appendKeys?: Set<string> }) {
    if (!this.activeParentPosts?.length) return;

    // The stored `reply_count` wins when the replies themselves weren't loaded (see
    // `deferRepliesInFeed`); the loaded list wins when it is ahead of it, e.g. a reply that
    // arrived through the live watcher a moment before the parent's count caught up.
    this.activeParentPosts.forEach((post) => {
      const loaded = this.activeParentThreadMap[post.key] ? this.activeParentThreadMap[post.key].length : 0;
      post.replyCount = Math.max(loaded, typeof post.reply_count === "number" ? post.reply_count : 0);
    });

    if (this.activeSortBy == "activity") {
      this.activeParentPosts.sort((a, b) => {
        const c = a.replyCount > b.replyCount ? -1 : a.replyCount < b.replyCount ? 1 : 0;
        return c !== 0 ? c : String(a.key).localeCompare(String(b.key));
      });
      return;
    }

    if (this.activeSortBy == "recentactivity") {
      const getActivityTimestamp = (post: any): number => {
        const replies = this.activeParentThreadMap[post.key] || [];
        const getTs = (dateObj: any): number => {
          if (dateObj == null) return 0;
          try {
            if (typeof dateObj.toDate === "function") {
              const t = dateObj.toDate().getTime();
              return Number.isFinite(t) ? t : 0;
            }
            const t = new Date(dateObj).getTime();
            return Number.isFinite(t) ? t : 0;
          } catch {
            return 0;
          }
        };
        // `last_activity_at` is the stored summary; loaded replies can only be newer than it.
        let latest = Math.max(getTs(post.created), getTs(post.last_activity_at));
        replies.forEach((r) => {
          const t = getTs(r.created);
          if (t > latest) latest = t;
        });
        return latest;
      };

      const compareRecentActivity = (a: any, b: any) => {
        const diff = getActivityTimestamp(b) - getActivityTimestamp(a);
        return diff !== 0 ? diff : String(a.key).localeCompare(String(b.key));
      };

      const appendKeys = options?.appendKeys;
      if (appendKeys) {
        const existing = this.activeParentPosts.filter((p) => appendKeys.has(p.key));
        const newlyLoaded = this.activeParentPosts.filter((p) => !appendKeys.has(p.key));
        newlyLoaded.sort(compareRecentActivity);
        this.activeParentPosts = [...existing, ...newlyLoaded];
      } else {
        this.activeParentPosts.sort(compareRecentActivity);
      }
      return;
    }

    this.activeParentPosts.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      if (a.timestamp) {
        const ts = a.timestamp > b.timestamp ? -1 : a.timestamp < b.timestamp ? 1 : 0;
        return ts !== 0 ? ts : String(a.key).localeCompare(String(b.key));
      } else if (a.created) {
        const aCreated = a.created?.toDate ? a.created.toDate() : a.created ? new Date(a.created) : new Date(0);
        const bCreated = b.created?.toDate ? b.created.toDate() : b.created ? new Date(b.created) : new Date(0);
        const dt = bCreated.getTime() - aCreated.getTime();
        return dt !== 0 ? dt : String(a.key).localeCompare(String(b.key));
      }
      return String(a.key).localeCompare(String(b.key));
    });
  }

  processPostsAndReplies(posts, clear?, storageKey?, channelId?: string, betaStringId?: string) {
    if (clear || !this.activeParentThreadMap) this.activeParentThreadMap = {};
    if (clear || !this.activeParentThreadMapTruncated) this.activeParentThreadMapTruncated = {};
    if (clear || !this.parentThreadKeys) this.parentThreadKeys = {};
    if (storageKey) {
      if (clear || !this.activeParentPostTypes[storageKey]) this.activeParentPostTypes[storageKey] = [];
    } else {
      if (clear || !this.activeParentPosts) this.activeParentPosts = [];
    }
    if (clear || !this.displayedPostsMap) this.displayedPostsMap = {};

    const recentActivityAppendKeys = !clear && !storageKey && this.activeSortBy === "recentactivity" && this.activeParentPosts?.length ? new Set(this.activeParentPosts.map((p) => p.key)) : null;

    if (this.userService.user?.id) {
      this.cachedNames[this.userService.user.id] = this.userService.user.firstName + " " + this.userService.user.lastName;
      this.cachedPhotos[this.userService.user.id] = this.userService.user.photoURL;
    }
    posts.forEach((item) => {
      this.cachedNames[item.author.id] = item.author.name;
      this.cachedPhotos[item.author.id] = item.author.photo;
      if (!item.parentID) this.parentThreadKeys[item.key] = true;
      this.buildHtmlComment(item, !this.isMobile);
      if (item.file) item.file.url = this.buildFileUrl(item.file.user_id, item.file);

      if (this._win.location.host == "nativescript" && item.created && item.created.seconds) item.created = moment.unix(item.created.seconds).toDate();
    });
    posts = posts.filter((item) => {
      return !item.parentID || this.parentThreadKeys[item.parentID];
    });

    const parentPosts = posts.filter((item) => !item.parentID);
    const replies = posts.filter((item) => item.parentID);

    let filteredParentPosts = parentPosts;
    if (betaStringId && channelId) {
      let channelIdString: string;
      if (typeof channelId === "string") {
        channelIdString = channelId;
      } else if (channelId && typeof channelId === "object") {
        channelIdString = (channelId as any).string_id || (channelId as any).id;
      } else {
        channelIdString = String(channelId);
      }
      if (channelIdString) {
        filteredParentPosts = this.betaCommunityService.filterPosts(parentPosts, channelIdString, this.userService.user?.id);
        const filteredParentKeys: any = {};
        filteredParentPosts.forEach((post) => {
          filteredParentKeys[post.key] = true;
        });
        const filteredReplies = replies.filter((reply) => filteredParentKeys[reply.parentID]);
        posts = [...filteredParentPosts, ...filteredReplies];
      } else {
        posts = [...parentPosts, ...replies];
      }
    } else {
      posts = [...parentPosts, ...replies];
    }

    if (posts && posts.length) {
      posts.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        const aCreated = a.created?.toDate ? a.created.toDate() : a.created ? new Date(a.created) : new Date(0);
        const bCreated = b.created?.toDate ? b.created.toDate() : b.created ? new Date(b.created) : new Date(0);
        return bCreated.getTime() - aCreated.getTime();
      });
      posts.forEach((item) => {
        if ((item.timestamp || item.created) && !item.parentID) {
          if (!this.activeParentThreadMap[item.key]) this.activeParentThreadMap[item.key] = [];
          if (!this.activeParentThreadMapTruncated[item.key]) this.activeParentThreadMapTruncated[item.key] = [];
          this.displayedPostsMap[item.key] = item;
          if (!item.parentID) {
            if (storageKey) {
              this.activeParentPostTypes[storageKey].push(item);
            } else {
              const alreadyListed = this.activeParentPosts.some((p) => p.key === item.key || p.id === item.id);
              if (!alreadyListed) {
                this.activeParentPosts.push(item);
              }
            }
          }
        } else {
          if (!this.activeParentThreadMap[item.parentID]) this.activeParentThreadMap[item.parentID] = [];
          if (!this.activeParentThreadMapTruncated[item.parentID]) this.activeParentThreadMapTruncated[item.parentID] = [];
          this.displayedPostsMap[item.key] = item;
          if (!this.activeParentThreadMap[item.parentID].filter((post) => post.id == item.id).length) this.activeParentThreadMap[item.parentID].push(item);
          if (this.activeParentThreadMapTruncated[item.parentID].length < 2 && !this.activeParentThreadMapTruncated[item.parentID].filter((post) => post.id == item.id).length)
            this.activeParentThreadMapTruncated[item.parentID].push(item); // this array contains only two replies.
        }
      });

      for (var i in this.activeParentThreadMapTruncated) {
        this.activeParentThreadMapTruncated[i].sort((a, b) => (a.created < b.created ? -1 : 1));
      }

      if (storageKey) {
        this.activeParentPostTypes[storageKey].sort((a, b) => {
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;
          if (a.timestamp) {
            return a.timestamp > b.timestamp ? -1 : 1;
          } else if (a.created) {
            const aCreated = a.created?.toDate ? a.created.toDate() : a.created ? new Date(a.created) : new Date(0);
            const bCreated = b.created?.toDate ? b.created.toDate() : b.created ? new Date(b.created) : new Date(0);
            return bCreated.getTime() - aCreated.getTime();
          }
        });
      }
      let shownPostsCount = 0;
      for (var x in this.activeParentThreadMap) {
        shownPostsCount++;
        this.activeParentThreadMap[x].sort((a, b) => {
          return a.created > b.created ? 1 : -1;
        });
      }
    }
    if (!storageKey) {
      this.sortActiveParentPosts({ appendKeys: recentActivityAppendKeys || undefined });
    }
  }

  getUserProfile(userId, documentReference, bypassCache = false) {
    if (this.cachedFirebaseProfiles[userId] && !bypassCache) {
      return new Promise((resolve, reject) => {
        this.cachedNames[userId] = this.cachedFirebaseProfiles[userId].firstName + " " + this.cachedFirebaseProfiles[userId].lastName;
        this.cachedPhotos[userId] = this.cachedFirebaseProfiles[userId].photo;
        resolve(this.cachedFirebaseProfiles[userId]);
      });
    } else {
      return documentReference.get().then((result) => {
        if (result.data()) {
          this.cachedFirebaseProfiles[userId] = result.data();
          this.cachedNames[userId] = this.cachedFirebaseProfiles[userId].firstName + " " + this.cachedFirebaseProfiles[userId].lastName;
          this.cachedPhotos[userId] = this.cachedFirebaseProfiles[userId].photo;
        }
        return result.data();
      });
    }
  }

  /**
   * Builds the HTML for a message body, with mentions turned into chips.
   *
   * Mentions are located by character offset and cut out FIRST, then each
   * remaining run of text is linkified and line-broken on its own. The old code
   * linkified the whole body and only afterwards ran `String.replace(selector)`
   * for each mention, which had four separate failure modes:
   *
   *   - a string needle replaces only the FIRST match, so mentioning the same
   *     person twice highlighted one of them;
   *   - two different people with the same display name both resolved to
   *     whichever record ran first;
   *   - "$&" or "$1" in a display name was interpreted as a replacement pattern;
   *   - a name containing something URL-shaped had already been rewritten into
   *     an anchor by `urlify`, so the selector no longer matched at all.
   *
   * Text runs are deliberately NOT escaped: older posts were persisted as editor
   * HTML, and escaping would surface their markup as literal text. The web
   * override sanitises the finished string instead.
   */
  renderMessageBody(raw: string, mentions: any[], makeEditable = false): string {
    const linkify = (text: string) => {
      let html = this.urlify(text);
      // One break per newline. `finalizeMessage` writes exactly one "\n" per line break in
      // the editor, so a blank line is "\n\n" — the old rule collapsed that to a single
      // <br />, which dropped every blank line on post, and again on every edit.
      html = html.replace(/\r\n|\r|\n/g, "<br />");
      return html;
    };

    const segments = segmentMessageByMentions(raw, mentions || []);

    return segments
      .map((segment) => {
        if (segment.type === "text") return linkify(segment.text);

        // A span rather than an <a href="javascript:void(0)">: it matches the
        // chip the editor inserts, so one CSS rule covers both, and it survives
        // sanitisation without a javascript: URL.
        const label = this.escapeHtml(segment.text);
        return (
          "<span" +
          (makeEditable ? ' contenteditable="false"' : "") +
          ' class="inline-mention font-weight-light" role="button" tabindex="0" data-mentionid="' +
          this.escapeHtml(String(segment.mention.id)) +
          '" aria-label="Mention ' +
          label +
          '">' +
          label +
          "</span>"
        );
      })
      .join("");
  }

  escapeHtml(value: string): string {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  buildHtmlComment(item, includeMentions = true, makeEditable = false) {
    // The signature (DENG-847) is set apart from the body: cut off the end here, rendered on
    // its own (`htmlSignature`). `item.message` itself is left whole — editing works on it.
    const { body, signature } = splitPostSignature(item.message || item.text, item.signature);
    item.noHtmlMessage = this.helperService.stripHtml(body, true);
    item.noHtmlMessageFlat = this.helperService.stripHtml(body, false, true);
    if (body) {
      item.htmlMessage = this.renderMessageBody(body, includeMentions ? item.mentions : null, makeEditable);
    } else if (signature) {
      item.htmlMessage = "";
    }
    item.htmlSignature = signature ? this.escapeHtml(signature).replace(/\r\n|\r|\n/g, "<br />") : "";
    item.htmlViewContent =
      '<span style="font-size: ' +
      (item.parentID ? "14" : "18") +
      "px; font-family: sans-serif; background-color: transparent; color: " +
      (this.helperService.mobileTheme == "dark" ? "white" : "#333") +
      ';">' +
      item.htmlMessage +
      "</span>";
    if (item.file) {
      item.file.url = this.buildFileUrl(item.file.user_id, item.file);
      item.files = [item.file];
    } else if (item.files) {
      item.files.sort((a, b) => {
        return a.extension > b.extension ? 1 : -1;
      });
    }

    if (item.articles) {
      item.articles.forEach((articleItem) => {
        if (articleItem.selector) {
          item.htmlMessage = item.htmlMessage.replace(
            articleItem.selector,
            "<span " +
              (makeEditable ? ' contenteditable="false"' : "") +
              ' class="inline-article" data-sectionid="' +
              articleItem.article.section_id +
              '" data-articleid="' +
              articleItem.article.id +
              '">' +
              articleItem.article.name +
              "</span>"
          );
        }
      });
    }
    if (item.public_file_links) {
      item.public_file_links.forEach((linkItem) => {
        if (linkItem.selector) {
          item.htmlMessage = item.htmlMessage.replace(
            linkItem.selector,
            "<span " +
              (makeEditable ? ' contenteditable="false"' : "") +
              ' class="inline-share" data-hash="' +
              linkItem.publicFile.hash +
              '" data-fileid="' +
              linkItem.publicFile.id +
              '">' +
              linkItem.publicFile.filename +
              "</span>"
          );
        }
      });
    }
    if (item.learn_content) {
      item.learn_content.forEach((contentItem) => {
        if (contentItem.selector) {
          if (contentItem.content.type == "video") {
            item.htmlMessage = item.htmlMessage.replace(
              contentItem.selector,
              "<span " + (makeEditable ? ' contenteditable="false"' : "") + 'class="inline-video" data-videoid="' + contentItem.content.id + '">' + contentItem.content.title + "</span>"
            );
          } else if (contentItem.content.type == "lesson") {
            item.htmlMessage = item.htmlMessage.replace(
              contentItem.selector,
              "<span " + (makeEditable ? ' contenteditable="false"' : "") + 'class="inline-video" data-lessonid="' + contentItem.content.id + '">' + contentItem.content.title + "</span>"
            );
          } else if (contentItem.content.tyoe == "course") {
            item.htmlMessage = item.htmlMessage.replace(
              contentItem.selector,
              "<span " + (makeEditable ? ' contenteditable="false"' : "") + 'class="inline-video" data-courseid="' + contentItem.content.id + '">' + contentItem.content.title + "</span>"
            );
          }
        }
      });
    }
    if (item.categories_or_sections) {
      item.categories_or_sections.forEach((contentItem) => {
        if (contentItem.selector) {
          let replaceHtml =
            "<span " +
            (makeEditable ? ' contenteditable="false"' : "") +
            ' class="inline-article" data-categoryid="' +
            contentItem.category_or_section.id +
            '">' +
            contentItem.category_or_section.name +
            "</span>";
          if (contentItem.category_or_section.category_id)
            replaceHtml =
              '<span class="inline-article" data-categoryid="' +
              contentItem.category_or_section.category_id +
              '" data-sectionid="' +
              contentItem.category_or_section.id +
              '">' +
              contentItem.category_or_section.name +
              "</span>";
          item.htmlMessage = item.htmlMessage.replace(contentItem.selector, replaceHtml);
        }
      });
    }

    if (item.embeds) {
      item.embeds = item.embeds.map((embed) => {
        if (typeof embed.src == "string") {
          return {
            ...embed,
            src: this.sanitizer.bypassSecurityTrustResourceUrl(embed.src),
          };
        } else {
          return embed;
        }
      });
    }

    return item;
  }

  // Supported services: YouTube, SoundCloud, Spotify
  parseEmbedUrls(text: string, compareArray: MessageEmbed[] = []) {
    if (text.includes("youtube.com/") || text.includes("youtu.be/") || text.includes("soundcloud.com/") || text.includes("open.spotify.com/")) {
      const urls = text.match(this.urlRegex) || [];
      const embeds: MessageEmbed[] = [];

      urls.forEach((url: string) => {
        let embedType: MessageEmbed["type"];
        const cleanUrl = url.replace(/&nbsp;?/g, "").trim();
        let id = cleanUrl;
        let embedSrc;
        let subtype = null;

        if (cleanUrl.includes("soundcloud.com/")) {
          // SoundCloud shortened urls can't be used for embedding.
          if (cleanUrl.includes("on.soundcloud.com")) return;
          embedType = "soundcloud";
          embedSrc = `https://w.soundcloud.com/player/?url=${cleanUrl}`;
        } else if (cleanUrl.includes("youtube.com/") || cleanUrl.includes("youtu.be/")) {
          embedType = "youtube";
          const parts = url.split("/");
          const idIndex = parts[2] == "youtu.be" ? 0 : parts[3].indexOf("v=") + 2;
          // youtube ids are 11 characters
          id = parts[3].substring(idIndex, idIndex + 11);
          embedSrc = `https://www.youtube.com/embed/${id}`;
        } else if (cleanUrl.includes("open.spotify.com/")) {
          const parts = url.split("/");
          const spotifyEmbedType = parts[3];
          const acceptedSpotifyEmbedTypes = ["track", "album", "artist", "episode", "show", "playlist"];

          if (spotifyEmbedType && acceptedSpotifyEmbedTypes.includes(spotifyEmbedType)) {
            id = parts[4].split("?")[0];

            if (id) {
              embedType = "spotify";
              embedSrc = `https://open.spotify.com/embed/${spotifyEmbedType}/${id}`;
              subtype = spotifyEmbedType;
            }
          }
        }

        if (embedType && compareArray.findIndex((embed) => embed.id == id) == -1 && embeds.findIndex((embed) => embed.id == id) == -1) {
          embeds.push({
            id,
            type: embedType,
            subtype,
            pastedUrl: cleanUrl,
            embedSrc,
          });
        }
      });

      return embeds;
    }
  }

  buildCommunityUserData(userData) {
    return {
      photo: userData.photo,
      cover_photo: userData.cover_photo,
      firstName: userData.firstName,
      lastName: userData.lastName,
      public: userData.public,
      tagline: userData.tagline,
      description: userData.description,
      id: userData.id,
      email: userData.email,
      hasSetupCommunity: userData.hasSetupCommunity,
      // Per-community onboarding-wizard completion map (2026-08-31, DENG-733-adjacent).
      // Must survive Firestore rehydration or a refresh strips it and every community's
      // wizard re-fires — same class of bug the `followingCommunities` line below fixed.
      hasSetupCommunities: userData.hasSetupCommunities,
      // The communities hub's onboarding: done (a timestamp), and the activation date the old
      // wizards stamped. Same rehydration rule — without them a refresh re-runs the takeover
      // for anyone who skipped every follow.
      communityOnboarded: userData.communityOnboarded,
      dateCommunityActivated: userData.dateCommunityActivated,
      status: userData.status,
      // Preserve the community-follow map — dropped previously because the whitelist
      // predated the feature. Without this, any refresh that goes through Firestore
      // (rather than the localStorage cache) would strip the map and cards on
      // /communities would render as not-following.
      followingCommunities: userData.followingCommunities,
      // Per-community notification preferences (DENG-733). Two maps:
      //   - `notificationPreferences` — channel prefs (email/mobile/desktop). Includes
      //     the legacy `community` / `beta_community` keys plus the nested
      //     `communities.{stringId}` sub-map for per-community channel overrides.
      //   - `communityNotificationPreferences` — topic prefs (new_posts, replies_to_me,
      //     replies_in_my_threads, mentions, reactions_on_my_posts), keyed by community string_id.
      // Both must be whitelisted or a Firestore refresh strips them and the bell modal
      // renders defaults every time — same class of bug the `followingCommunities`
      // line above fixed.
      notificationPreferences: userData.notificationPreferences,
      communityNotificationPreferences: userData.communityNotificationPreferences,
      // Per-community post signatures (DENG-847), keyed by community string_id. Same
      // whitelist rule as the two maps above — omit it and a Firestore-backed refresh
      // silently wipes everyone's signature.
      communitySignatures: userData.communitySignatures,
      // The signature for every community without its own — same whitelist rule.
      defaultCommunitySignature: userData.defaultCommunitySignature,
      // Per-channel "seen up to here" marks behind the new-post indicators, shaped
      // `{ [communityStringId]: { [channelId]: epochMillis } }`. Same whitelist rule again:
      // dropped here, a refresh resets every mark to zero and every channel lights up as
      // though it were all new.
      communityLastSeen: userData.communityLastSeen,
      // Feed layout per community ("list" / "detailed"), moved out of localStorage so the
      // choice follows the member between devices. Same whitelist rule as everything above.
      communityViewModes: userData.communityViewModes,
      // Feed ordering per community ("recentactivity" / "date"). Whitelisted for the same
      // reason as everything above it — omitted, a refresh silently resets the choice.
      communitySortModes: userData.communitySortModes,
      // Settings for all communities (notification channels and topics, signature, feed
      // order and layout) — the fallback every per-community setting resolves to. Same
      // whitelist rule: omitted, a refresh drops the member back to the built-in defaults.
      communityDefaults: userData.communityDefaults,
      // Starred channels, `{ [communityStringId]: { [channelStringId]: epochMillis } }` — the
      // hub's Favorite channels rail. Same whitelist rule as everything above.
      favoriteChannels: userData.favoriteChannels,
    };
  }
}
